import React, { useEffect, useRef, useMemo } from "react";

import useTranslations from "../../../hooks/useTranslations";

import { TemplateQuestionFragment } from "../../../graphql/fragments";
import { Textarea, FormEntry, Input } from "../../wombat-ui";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";
import Answer from "./utils/Answer";
import { ReportAnswer } from "../../../graphql/types";

type TemplateQuestionTextLongValue = string;

type TemplateQuestionTextLong = {
  defaultValue: TemplateQuestionTextLongValue;
} & TemplateQuestionFragment;

export function isTemplateQuestionTextLong(
  q: TemplateQuestionFragment
): q is TemplateQuestionTextLong {
  return q.type === "TemplateQuestionTextLong";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionTextLong {
  return question as TemplateQuestionTextLong;
}

type ReportAnswerTextLong = {
  value: TemplateQuestionTextLongValue;
} & ReportAnswer;

export function parseAnswers(answers: ReportAnswer[]): ReportAnswerTextLong[] {
  return answers as ReportAnswerTextLong[];
}

type TemplateQuestionTextLongProps = TemplateQuestionImplProps;

export const TemplateQuestionTextLong: React.FC<TemplateQuestionTextLongProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    errors,
    status,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );

    const inputRef = useRef(null);
    useEffect(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [inputRef.current, autoFocus]);

    return (
      <div>
        <FormEntry
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
              description={question.description}
              referents={question.referents}
            />
          }
          description={t("reports.textLongQuestion") as string}
        >
          {needsToBeFilled && (
            <Textarea
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
              }
              label={question.label}
              id={question.id}
              error={errors?.value}
              name={`${name}.value`}
              placeholder={t("reports.textLongPlaceholder") as string}
              ref={(ref) => {
                inputRef.current = ref;
                register(ref);
              }}
              srOnlyLabel
            />
          )}
        </FormEntry>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            disabled={!needsToBeFilled}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
          />
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer answer={a} answerAs={a.value} key={a.id} />
              ))}
            </div>
          </div>
        )}
        {status !== "to_be_filled_evaluator" && answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer answer={a} answerAs={a.value} key={a.id} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionTextLongPreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionTextLongPreview: React.FC<TemplateQuestionTextLongPreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <FormEntry
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
            />
          }
          description={t("reports.textLongQuestion") as string}
        >
          <Textarea
            label={question.label}
            id={question.id}
            srOnlyLabel
            disabled
          />
        </FormEntry>
      </div>
    );
  }
);
